/**
 * Chest loot tables (vanilla-ish, trimmed to the items this engine has).
 *
 * `rollChestLoot(rnd, kind)` returns exactly 27 Stacks (empties included), the
 * rolled items scattered over random slots like vanilla's shuffle.
 *
 * Which `kind` for which structure (src/worldgen/structures.ts places a bare
 * CHEST_ID, so the lead derives the kind from the chest's position/biome or
 * just uses 'default'): village houses/smithy -> 'village', dungeon ->
 * 'dungeon', mineshaft corridors -> 'mineshaft', shipwreck -> 'shipwreck',
 * desert pyramid -> 'desert_temple', jungle temple -> 'jungle_temple',
 * ruined portal -> 'ruined_portal', outpost -> 'outpost', ocean ruin ->
 * 'ocean_ruin', buried treasure -> 'buried_treasure', swamp hut -> 'default'.
 */

import { ANCIENT_DEBRIS_ID, NETHER_WART_IDS, COBWEB_ID, OBSIDIAN_ID, OAK_SAPLING_ID, RAIL_ID, TNT_ID, TORCH_ID } from '../blocks';
import { I } from './itemIds';
import { maxStackOf } from './itemdb';
import { emptyStack, stackOf, type Stack } from './types';

export type LootKind =
  | 'default' | 'dungeon' | 'village' | 'mineshaft' | 'shipwreck'
  | 'desert_temple' | 'jungle_temple' | 'ruined_portal' | 'outpost' | 'ocean_ruin' | 'buried_treasure'
  | 'nether_bridge'
  | 'bastion_treasure' | 'bastion_other' | 'bastion_bridge' | 'bastion_hoglin_stable'
  | 'trial_chambers' | 'vault' | 'vault_ominous' | 'end_city' | 'end_ship';

export const CHEST_SLOTS = 27;

/** [item id, weight, min count, max count] */
type Entry = readonly [number, number, number, number];
interface Pool { rolls: readonly [number, number]; entries: readonly Entry[] }

const e = (id: number, w: number, lo = 1, hi = lo): Entry => [id, w, lo, hi];

const TABLES: Record<LootKind, readonly Pool[]> = {
  // --- structures round 2 (Bedrock 1.21 tables, trimmed to the items this engine has) ---
  /** chests/bastion/treasure: ancient debris, netherite scrap, gold and gilded blocks. */
  bastion_treasure: [
    { rolls: [3, 3], entries: [
      e(I.NETHERITE_INGOT, 15), e(OBSIDIAN_ID, 6, 2, 4), e(I.NETHERITE_SCRAP, 8), e(I.DIAMOND, 6, 2, 6),
      e(I.GOLD_INGOT, 10, 2, 8), e(I.IRON_INGOT, 10, 1, 6), e(I.GOLDEN_SWORD, 6), e(I.GOLDEN_CHESTPLATE, 5),
      e(I.GOLDEN_HELMET, 5), e(I.GOLDEN_LEGGINGS, 5), e(I.GOLDEN_BOOTS, 5), e(I.DIAMOND_SWORD, 5),
      e(I.DIAMOND_CHESTPLATE, 5), e(I.DIAMOND_HELMET, 5), e(I.DIAMOND_BOOTS, 5), e(I.DIAMOND_LEGGINGS, 5),
      e(I.GOLDEN_APPLE, 3), e(ANCIENT_DEBRIS_ID, 12, 1, 2),
    ] },
    { rolls: [4, 4], entries: [
      e(I.GOLD_NUGGET, 20, 2, 8), e(I.QUARTZ, 20, 4, 10), e(I.GLOWSTONE_DUST, 15, 3, 6), e(I.MAGMA_CREAM, 15, 2, 6),
      e(I.ENDER_PEARL, 10, 4, 8), e(I.STRING, 10, 3, 6), e(I.FIRE_CHARGE, 10, 2, 4), e(I.GOLD_INGOT, 5, 2, 8),
    ] },
    { rolls: [1, 1], entries: [e(I.NETHERITE_UPGRADE_SMITHING_TEMPLATE, 1)] },
  ],
  bastion_other: [
    { rolls: [1, 1], entries: [
      e(I.DIAMOND_PICKAXE, 6), e(I.DIAMOND_SHOVEL, 6), e(I.IRON_SWORD, 6), e(I.GOLDEN_SWORD, 6),
      e(I.GOLDEN_CHESTPLATE, 6), e(I.CROSSBOW, 6), e(I.SHIELD, 6),
    ] },
    { rolls: [2, 4], entries: [
      e(I.GOLD_INGOT, 8, 2, 8), e(I.IRON_INGOT, 8, 1, 4), e(I.GOLD_NUGGET, 10, 2, 8), e(I.STRING, 5, 1, 6),
      e(I.LEATHER, 5, 1, 3), e(I.ARROW, 10, 5, 17), e(I.COOKED_PORKCHOP, 5, 1, 3), e(I.BONE, 4, 1, 3),
    ] },
    { rolls: [1, 1], entries: [e(I.NETHERITE_SCRAP, 1), e(OBSIDIAN_ID, 4, 2, 4), e(ANCIENT_DEBRIS_ID, 1)] },
  ],
  bastion_bridge: [
    { rolls: [1, 1], entries: [e(I.GOLDEN_SWORD, 1), e(I.CROSSBOW, 1)] },
    { rolls: [1, 2], entries: [e(I.GOLD_INGOT, 4, 1, 3), e(I.IRON_INGOT, 3, 1, 3), e(I.GOLD_NUGGET, 6, 1, 6), e(I.LEATHER, 2, 1, 2)] },
    { rolls: [2, 4], entries: [e(I.STRING, 5, 1, 6), e(I.LEATHER, 4, 1, 3), e(I.ARROW, 8, 5, 17), e(I.GOLD_NUGGET, 5, 1, 6)] },
    { rolls: [1, 1], entries: [e(I.NETHERITE_SCRAP, 1), e(OBSIDIAN_ID, 6, 2, 4), e(I.GOLDEN_APPLE, 3)] },
  ],
  bastion_hoglin_stable: [
    { rolls: [1, 1], entries: [e(I.DIAMOND_SHOVEL, 1), e(I.DIAMOND_PICKAXE, 1), e(I.GOLDEN_CHESTPLATE, 1)] },
    { rolls: [2, 4], entries: [e(I.GOLD_INGOT, 6, 2, 8), e(I.GOLD_NUGGET, 8, 2, 8), e(I.STRING, 4, 1, 6), e(I.LEATHER, 4, 1, 3), e(I.PORKCHOP, 4, 1, 4)] },
    { rolls: [1, 1], entries: [e(I.NETHERITE_SCRAP, 1), e(ANCIENT_DEBRIS_ID, 1), e(OBSIDIAN_ID, 3, 2, 4)] },
  ],
  /** chests/trial_chambers (corridor, supply): modest basics plus the breeze rod. */
  trial_chambers: [{
    rolls: [2, 4],
    entries: [
      e(I.ARROW, 10, 4, 14), e(I.IRON_INGOT, 8, 1, 4), e(I.COPPER_INGOT, 10, 2, 8),
      e(I.BREAD, 8, 1, 3), e(I.COOKED_BEEF, 6, 1, 3), e(I.STRING, 6, 1, 4), e(I.BONE, 6, 1, 4),
      e(I.BREEZE_ROD, 3, 1, 2), e(I.EMERALD, 4, 1, 3), e(I.DIAMOND, 1), e(I.SADDLE, 1), e(I.GOLDEN_APPLE, 1),
    ],
  }],
  /** vaults, rolled by game/trials.ts when a trial key opens one. */
  vault: [{
    rolls: [2, 3],
    entries: [
      e(I.EMERALD, 12, 2, 6), e(I.DIAMOND, 4, 1, 2), e(I.IRON_INGOT, 10, 3, 8), e(I.COPPER_INGOT, 10, 4, 10),
      e(I.GOLDEN_APPLE, 3), e(I.CROSSBOW, 4), e(I.BOW, 4), e(I.ARROW, 8, 8, 16), e(I.SHIELD, 3), e(I.BREEZE_ROD, 3, 1, 3),
      e(I.ENCHANTED_GOLDEN_APPLE, 1),
    ],
  }],
  /** ominous vault: the heavy core (and so the mace) lives here. */
  vault_ominous: [{
    rolls: [2, 3],
    entries: [
      e(I.EMERALD, 10, 4, 10), e(I.DIAMOND, 8, 1, 3), e(I.HEAVY_CORE, 5), e(I.ENCHANTED_GOLDEN_APPLE, 3),
      e(I.GOLDEN_APPLE, 6, 1, 2), e(I.NETHERITE_SCRAP, 2), e(I.TRIDENT, 1), e(I.CROSSBOW, 4),
    ],
  }],
  end_city: [{
    rolls: [2, 6],
    entries: [
      e(I.DIAMOND, 5, 2, 7), e(I.IRON_INGOT, 8, 4, 8), e(I.GOLD_INGOT, 15, 2, 7), e(I.EMERALD, 2, 2, 6),
      e(I.BEETROOT_SEEDS, 5, 1, 10), e(I.SADDLE, 3), 
      e(I.DIAMOND_SWORD, 3), e(I.DIAMOND_BOOTS, 3), e(I.DIAMOND_CHESTPLATE, 3), e(I.DIAMOND_LEGGINGS, 3),
      e(I.DIAMOND_HELMET, 3), e(I.DIAMOND_PICKAXE, 3), e(I.DIAMOND_SHOVEL, 3),
      e(I.IRON_SWORD, 3), e(I.IRON_BOOTS, 3), e(I.IRON_CHESTPLATE, 3), e(I.IRON_LEGGINGS, 3), e(I.IRON_HELMET, 3),
    ],
  }],
  end_ship: [{
    rolls: [2, 6],
    entries: [
      e(I.DIAMOND, 5, 2, 7), e(I.IRON_INGOT, 8, 4, 8), e(I.GOLD_INGOT, 15, 2, 7), e(I.EMERALD, 2, 2, 6),
      e(I.BEETROOT_SEEDS, 5, 1, 10), e(I.SADDLE, 3), e(I.DIAMOND_SWORD, 3), e(I.DIAMOND_CHESTPLATE, 3),
    ],
   }, { rolls: [1, 1], entries: [e(I.ELYTRA, 1)] }],
  /** Vanilla chests/nether_bridge (2-4 rolls; golden horse armor omitted). The
   *  netherite upgrade template (weight 3) is a NON-VANILLA stand-in until
   *  bastion remnants exist — vanilla only has it in bastion chests. */
  nether_bridge: [{
    rolls: [2, 4],
    entries: [
      e(I.DIAMOND, 5, 1, 3), e(I.IRON_INGOT, 5, 1, 5), e(I.GOLD_INGOT, 15, 1, 3), e(I.GOLDEN_SWORD, 5),
      e(I.GOLDEN_CHESTPLATE, 5), e(I.FLINT_AND_STEEL, 5), e(NETHER_WART_IDS[0], 5, 3, 7), e(I.SADDLE, 10),
      e(OBSIDIAN_ID, 2, 2, 4), e(I.NETHERITE_UPGRADE_SMITHING_TEMPLATE, 3),
    ],
  }],
  default: [{
    rolls: [3, 7],
    entries: [
      e(I.IRON_INGOT, 10, 1, 5), e(I.GOLD_INGOT, 5, 1, 3), e(I.COAL, 15, 1, 4), e(I.BREAD, 15, 1, 3),
      e(I.STICK, 10, 1, 6), e(I.APPLE, 10, 1, 3), e(I.STRING, 5, 1, 4), e(I.BONE, 5, 1, 4),
      e(I.ROTTEN_FLESH, 5, 1, 4), e(I.ARROW, 5, 2, 8), e(I.DIAMOND, 1), e(I.SADDLE, 2), e(I.GOLDEN_APPLE, 1),
    ],
  }],
  village: [{
    rolls: [3, 8],
    entries: [
      e(I.DIAMOND, 3, 1, 3), e(I.IRON_INGOT, 10, 1, 5), e(I.GOLD_INGOT, 5, 1, 3), e(I.BREAD, 15, 1, 4),
      e(I.APPLE, 15, 1, 5), e(I.IRON_PICKAXE, 5), e(I.IRON_SWORD, 5), e(I.IRON_CHESTPLATE, 5),
      e(I.IRON_HELMET, 5), e(I.IRON_LEGGINGS, 5), e(I.IRON_BOOTS, 5), e(OBSIDIAN_ID, 5, 3, 7),
      // spears: weaponsmith chest only (not sure of the vanilla weights for the other tables)
      e(I.WOODEN_SPEAR, 4), e(I.STONE_SPEAR, 3), e(I.IRON_SPEAR, 3),
      e(OAK_SAPLING_ID, 5, 3, 7), e(I.SADDLE, 3), e(I.WHEAT, 20, 1, 4), e(I.COAL, 6, 1, 4), e(I.BREAD, 10, 1, 2),
    ],
  }],
  dungeon: [
    {
      rolls: [1, 3],
      entries: [e(I.SADDLE, 20), e(I.GOLDEN_APPLE, 15), e(I.IRON_INGOT, 10, 1, 4), e(I.GOLD_INGOT, 5, 1, 4), e(I.DIAMOND, 3)],
    },
    {
      rolls: [3, 3],
      entries: [
        e(I.BREAD, 20), e(I.WHEAT, 20, 1, 4), e(I.BUCKET, 10), e(I.REDSTONE, 15, 1, 4), e(I.COAL, 15, 1, 4),
        e(I.WHEAT_SEEDS, 10, 2, 4), e(I.IRON_INGOT, 10, 1, 4), e(I.NAME_TAG, 2),
      ],
    },
    {
      rolls: [3, 4],
      entries: [e(I.BONE, 10, 1, 8), e(I.GUNPOWDER, 10, 1, 8), e(I.ROTTEN_FLESH, 10, 1, 8), e(I.STRING, 10, 1, 8)],
    },
  ],
  mineshaft: [
    {
      rolls: [1, 2],
      entries: [
        e(I.GOLDEN_APPLE, 20), e(I.IRON_INGOT, 10, 1, 5), e(I.GOLD_INGOT, 5, 1, 3), e(I.REDSTONE, 5, 4, 9),
        e(I.LAPIS_LAZULI, 5, 4, 9), e(I.DIAMOND, 3, 1, 2), e(I.COAL, 10, 3, 8), e(I.BREAD, 15, 1, 3),
        e(I.WHEAT_SEEDS, 10, 2, 4), e(I.IRON_PICKAXE, 5),
      ],
    },
    {
      rolls: [3, 3],
      entries: [e(RAIL_ID, 20, 4, 8), e(TORCH_ID, 15, 1, 16), e(COBWEB_ID, 5, 1, 2), e(I.STICK, 5, 1, 4), e(I.BREAD, 5)],
    },
    {
      rolls: [0, 1],
      entries: [e(I.NAME_TAG, 1)],
    },
  ],
  shipwreck: [
    {
      rolls: [3, 6],
      entries: [
        e(I.IRON_INGOT, 90, 1, 5), e(I.GOLD_INGOT, 10, 1, 5), e(I.EMERALD, 40, 1, 5), e(I.DIAMOND, 5),
        e(I.WHEAT, 7, 8, 21), e(I.COAL, 6, 2, 8), e(I.ROTTEN_FLESH, 5, 5, 24), e(I.STRING, 4, 1, 4), e(I.BREAD, 5, 1, 3),
        e(I.LEATHER_HELMET, 3), e(I.LEATHER_CHESTPLATE, 3), e(I.LEATHER_LEGGINGS, 3), e(I.LEATHER_BOOTS, 3),
      ],
    },
    { rolls: [1, 2], entries: [e(I.IRON_NUGGET, 20, 1, 10), e(I.GOLD_NUGGET, 10, 1, 10), e(TNT_ID, 5, 1, 2), e(I.ARROW, 10, 3, 8)] },
  ],
  desert_temple: [
    {
      rolls: [2, 4],
      entries: [
        e(I.DIAMOND, 5, 1, 3), e(I.IRON_INGOT, 15, 1, 5), e(I.GOLD_INGOT, 15, 2, 7), e(I.EMERALD, 15, 1, 3),
        e(I.BONE, 25, 4, 6), e(I.SPIDER_EYE, 25, 1, 3), e(I.ROTTEN_FLESH, 25, 3, 7), e(I.SADDLE, 20),
        e(I.GOLDEN_APPLE, 2),
      ],
    },
    { rolls: [4, 4], entries: [e(I.BONE, 10, 1, 8), e(I.GUNPOWDER, 10, 1, 8), e(I.ROTTEN_FLESH, 10, 1, 8), e(I.STRING, 10, 1, 8)] },
  ],
  jungle_temple: [{
    rolls: [2, 6],
    entries: [
      e(I.DIAMOND, 3, 1, 3), e(I.IRON_INGOT, 50, 1, 5), e(I.GOLD_INGOT, 15, 2, 7), e(I.EMERALD, 2, 1, 3),
      e(I.BONE, 20, 4, 6), e(I.ROTTEN_FLESH, 16, 3, 7), e(I.SADDLE, 3), e(I.ARROW, 8, 3, 8),
    ],
  }],
  ruined_portal: [{
    rolls: [4, 8],
    entries: [
      e(I.IRON_NUGGET, 40, 9, 18), e(I.FLINT, 40, 1, 4), e(OBSIDIAN_ID, 40, 1, 2), e(I.GOLD_NUGGET, 15, 4, 24),
      e(I.GOLDEN_APPLE, 15), e(I.GOLDEN_SWORD, 15), e(I.GOLDEN_AXE, 15), e(I.GOLDEN_HOE, 15),
      e(I.GOLDEN_SHOVEL, 15), e(I.GOLDEN_PICKAXE, 15), e(I.GOLDEN_HELMET, 15), e(I.GOLDEN_CHESTPLATE, 15),
      e(I.GOLDEN_LEGGINGS, 15), e(I.GOLDEN_BOOTS, 15),
    ],
  }],
  outpost: [
    {
      rolls: [0, 1],
      entries: [e(I.BOW, 1)],
    },
    {
      rolls: [2, 3],
      entries: [e(I.WHEAT, 7, 3, 5), e(I.BREAD, 7, 1, 3), e(I.COAL, 7, 3, 5), e(I.IRON_INGOT, 3, 1, 3), e(I.ARROW, 4, 2, 6), e(I.STICK, 5, 1, 3)],
    },
    { rolls: [1, 3], entries: [e(I.STRING, 5, 1, 3), e(I.GUNPOWDER, 5, 1, 3), e(I.BONE, 5, 1, 3), e(I.IRON_INGOT, 1, 1, 2)] },
  ],
  ocean_ruin: [{
    rolls: [2, 8],
    entries: [
      e(I.COAL, 15, 1, 4), e(I.GOLD_NUGGET, 10, 1, 3), e(I.EMERALD, 1), e(I.WHEAT, 10, 2, 3),
      e(I.LEATHER, 5), e(I.GOLDEN_APPLE, 1), e(I.CLAY_BALL, 5, 1, 3), e(I.STICK, 5, 1, 3),
    ],
  }],
  buried_treasure: [
    {
      rolls: [5, 8],
      entries: [
        e(I.IRON_INGOT, 20, 1, 4), e(I.GOLD_INGOT, 10, 1, 4), e(I.EMERALD, 5, 4, 8), e(I.DIAMOND, 5, 1, 2),
        e(I.COOKED_BEEF, 10, 2, 4), e(I.COOKED_PORKCHOP, 10, 2, 4), e(I.COOKED_CHICKEN, 10, 2, 4), e(TNT_ID, 5, 1, 2),
      ],
    },
    { rolls: [1, 1], entries: [e(I.IRON_CHESTPLATE, 1), e(I.IRON_SWORD, 1), e(I.DIAMOND, 1, 1, 2), e(I.GOLDEN_APPLE, 1)] },
  ],
};

function pick(entries: readonly Entry[], total: number, rnd: () => number): Entry {
  let r = rnd() * total;
  for (const en of entries) {
    r -= en[1];
    if (r < 0) return en;
  }
  return entries[entries.length - 1];
}

export function rollChestLoot(rnd: () => number, kind: LootKind = 'default'): Stack[] {
  const items: Stack[] = [];
  for (const pool of TABLES[kind] ?? TABLES.default) {
    const total = pool.entries.reduce((a, en) => a + en[1], 0);
    const rolls = pool.rolls[0] + Math.floor(rnd() * (pool.rolls[1] - pool.rolls[0] + 1));
    for (let i = 0; i < rolls; i++) {
      const [id, , lo, hi] = pick(pool.entries, total, rnd);
      const count = Math.min(maxStackOf(id) || 64, lo + Math.floor(rnd() * (hi - lo + 1)));
      // merge into an existing stack of the same stackable id, as vanilla does
      const same = items.find((s) => s.id === id && maxStackOf(id) > 1 && s.count + count <= maxStackOf(id));
      if (same) same.count += count;
      else items.push(stackOf(id, count));
    }
  }
  const out: Stack[] = Array.from({ length: CHEST_SLOTS }, emptyStack);
  // vanilla scatters loot over random free slots (Fisher-Yates over slot indices)
  const slots = Array.from({ length: CHEST_SLOTS }, (_, i) => i);
  for (let i = slots.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [slots[i], slots[j]] = [slots[j], slots[i]];
  }
  items.slice(0, CHEST_SLOTS).forEach((s, i) => { out[slots[i]] = s; });
  return out;
}
